import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ShieldAlert, AlertTriangle, Clock, FileText, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { fmtDate, fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

import BudgetVsActualChart from "@/components/project/BudgetVsActualChart";
import ProjectOverviewCharts from "@/components/project/ProjectOverviewCharts";
import Project360Overview from "@/components/project/Project360Overview";
function pct1(v) {
  return v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`;
}

export default function Project360() {
  const { projectId, project, projectRole } = useProjectCtx();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/360`);
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <p className="text-sm text-muted-foreground" data-testid="project-360-loading">Loading Project 360…</p>;

  const { header, tiles, progress, budget_vs_actual, recent_cost_entries, latest_daily_reports, open_issues, open_delays, open_alerts, procurement_overdue, upcoming_milestones } = data;

  const progressRows = [
    { label: "Engineering", planned: progress.engineering_planned_pct, actual: progress.engineering_actual_pct },
    { label: "Procurement", planned: progress.procurement_planned_pct, actual: progress.procurement_actual_pct },
    { label: "Construction", planned: progress.construction_planned_pct, actual: progress.construction_actual_pct },
    { label: "Overall", planned: progress.overall_planned_pct, actual: progress.overall_actual_pct },
  ];

  return (
    <div className="space-y-6" data-testid="project-360-page">
      <Project360Overview data={data} project={project} projectId={projectId} projectRole={projectRole} onRefresh={load} />

    </div>
  );
}

function Tile({ label, value, tone }) {
  const toneClass = tone === "rose" ? "text-rose-400" : tone === "emerald" ? "text-emerald-400" : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`project-360-tile-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-2 font-mono text-lg font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function ListCard({ title, icon: Icon, testId, items, empty, render, onClick }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid={testId}>
      <button onClick={onClick} className="flex w-full items-center justify-between border-b border-border p-4 text-left hover:bg-accent/30" data-testid={`${testId}-header`}>
        <span className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {title}</span>
      </button>
      {items.length === 0 ? (
        <p className="p-4 text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.slice(0, 6).map((item, i) => (
            <li key={item.id || i} className="p-3 text-xs text-foreground">{render(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
